import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import moment from "moment";
import { ActionMeta, SingleValue } from "react-select";
import { toast } from "react-toastify";
import { isEqual } from "lodash";
import TextInput from "../../components/text-input/text-input";
import RowRadioButtonsGroup from "../../components/radio-button";
import ApplicantPhoto from "./applicant-photo";
import { API_CONSTANTS, VALIDATION_REGEXPS } from "../../constants";
import "./applicant-form.css";
import {
	ApplicantBasicDetailsFormTypes,
	EMPTY_BASIC_DETAILS,
} from "../../utils/booking-utils";
import { convertToTitleCase, getFileType } from "../../utils/functions";
import DatePickerComponent from "../../components/date-picker";
import { YYYY_MM_DD_WITH_HYPHEN } from "../../utils/date";
import Dropdown from "../../components/dropdown";
import { useFetchSalutationQuery } from "../../../features/api/booking-api-slice";
import { useGetUserDetailsHooks, useRedirectToLogin } from "../../hooks";
import { FormGroup } from "@mui/material";
import { Label, StyledCheckBox } from "./styled-component";
import { CircleOutlined } from "@mui/icons-material";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import Colors from "../../utils/theme/color";
import { useLocation } from "react-router-dom";

const MINIMUM_DOB = "1900-01-01";
const MAXIMUM_PIN_LENGTH = 6;

const ApplicantBasicDetailsForm = ({
	basicData = {},
	numberOfApplicant = 0,
	onUpdate,
	isGenderRequired = true,
	isFatherNameRequired = true,
	isDOBRequired = true,
	isAddressRequired = true,
	isPhotoRequired = true,
	isEmailMandatory = false,
	styles = {},
	isEditable = true,
	savePhoto = () => { },
	isSalutationRequired = false,
}: ApplicantBasicDetailsFormTypes) => {
	const genders = [
		{ id: "Male", label: "Male" },
		{ id: "Female", label: "Female" },
	];

	const { t } = useTranslation();
	const location = useLocation();
	const { getUserToken } = useGetUserDetailsHooks();
	const { redirectToLoginBasedOnResponse } = useRedirectToLogin();

	const [basicDetails, setBasicDetails] = useState(basicData);
	const basicDetailsRef = useRef(basicDetails);
	const [salutationList, setSalutationList] = useState([]);
	const [canEdit, setCanEdit] = useState(false);
	const [isAltMobileFocused, setIsAltMobileFocused] = useState(false);
	const isEditMode = canEdit;

	useEffect(() => {
		const path = location.pathname;
		const lastSegment = path.split("/").pop(); // gets 'new' or 'edit'
		if (lastSegment === "edit") {
			setCanEdit(true);
		} else if (lastSegment === "new") {
			setCanEdit(false);
		}
	}, [location.pathname]);

	useEffect(() => {
		if (basicData && (basicData?.name || basicData?.firstName)) {
			if (isEqual(basicData, basicDetailsRef.current)) {
				return;
			}
			const defaultType = basicData?.agreementAddressType || "Correspondence";
			const updatedData = {
				...basicData,
				agreementAddressType: defaultType,
			};
			setBasicDetails(updatedData);
			basicDetailsRef.current = updatedData;
		}
	}, [basicData]);

	useEffect(() => {
		if (!Object.is(basicDetails, EMPTY_BASIC_DETAILS)) {
			basicDetailsRef.current = basicDetails;
			onUpdate(basicDetails);
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [basicDetails]);

	const isAddressSame = useCallback(
		() => basicDetails?.isPermanentAddressSameAsCorrespondence === "YES",
		[basicDetails?.isPermanentAddressSameAsCorrespondence]
	);

	const isAddressSameAsPrimaryApplicant = useCallback(
		() =>
			numberOfApplicant > 1 &&
			basicDetails?.isAddressSameAsPrimaryApplicant === "YES",
		[basicDetails?.isAddressSameAsPrimaryApplicant, numberOfApplicant]
	);

	const hasAlt = useMemo(
		() => Boolean(`${basicDetails?.alternativeMobile ?? ""}`.trim()),
		[basicDetails?.alternativeMobile]
	);

	const lockAltRef = useRef(!!basicData?.alternativeMobile?.trim()); // snapshot at mount
	const lockAlt = lockAltRef.current;

	const altDisabled = !isEditable || (isEditMode && lockAlt);

	const maskPhone = (num?: string) => {
		if (!num) return "";
		const s = String(num).replace(/\D/g, "");
		return s.length <= 4 ? s : `XXXXXX${s.slice(-4)}`;
	};

	const maskAltPhone = (num?: string) => {
		if (!num) return "";
		const s = String(num).replace(/\D/g, "");
		return s.length <= 4 ? s : `XXXXXX${s.slice(-4)}`;
	};

	const maskEmail = (email?: string) => {
		if (!email) return "";

		const [local = "", domain = ""] = String(email).split("@");

		// If local part has <= 5 characters, mask all except last 3
		if (local.length <= 5) {
			const visible = local.slice(-3);
			const masked = "X".repeat(Math.max(local.length - 3, 0)) + visible;
			return domain ? `${masked}@${domain}` : masked;
		}

		// Otherwise: mask only the first 5 chars, keep the rest
		const maskedLocal = "X".repeat(5) + local.slice(5);
		// Ensure last 3 actual chars are always shown
		const last3 = local.slice(-3);
		const prefix = maskedLocal.slice(0, maskedLocal.length - 3);

		return domain ? `${prefix}${last3}@${domain}` : `${prefix}${last3}`;
	};

	const isAltRequired = isEditMode && !lockAlt;
	const altRequiredError =
		isAltRequired && !`${basicDetails?.alternativeMobile ?? ""}`.trim();

	const lockMobileRef = useRef(!!basicData?.mobile?.trim());
	const lockEmailRef = useRef(!!basicData?.email?.trim());
	const lockMobile = lockMobileRef.current;
	const lockEmail = lockEmailRef.current;

	// keep your display logic the same
	const mobileDisplay = isEditMode
		? maskPhone(basicDetails?.mobile)
		: basicDetails?.mobile;

	const emailDisplay = isEditMode
		? maskEmail(basicDetails?.email) // masked in edit
		: lockEmail
			? maskEmail(basicDetails?.email)
			: basicDetails?.email;

	const altMobileDisplay = altDisabled
		? maskAltPhone(basicDetails?.alternativeMobile)
		: basicDetails?.alternativeMobile || "";

	useEffect(() => {
		if (isAddressSame()) {
			setBasicDetails({
				...basicDetailsRef?.current,
				permanentAddress: basicDetailsRef?.current.correspondenceAddress,
				permanentPin: basicDetailsRef?.current.pin,
				permanentTelephone: basicDetailsRef?.current.telephone,
			});
		}
	}, [isAddressSame]);

	const {
		data: getSalutationList,
		isSuccess: isGetSalutationListSuccess,
		isError: isGetSalutationListFailed,
		error: getSalutationListError,
		refetch: refetchSalutationList,
	} = useFetchSalutationQuery(
		{
			userToken: getUserToken(),
		},
		{ skip: !isSalutationRequired }
	);

	useEffect(() => {
		if (isGetSalutationListSuccess) {
			if (
				getSalutationList?.data?.length &&
				getSalutationList?.status_code === API_CONSTANTS.successStatusCode
			) {
				setSalutationList(getSalutationList?.data);
			} else if (getSalutationList?.message) {
				toast(getSalutationList?.message, {
					type: "error",
				});
				setSalutationList([]);
			}
		} else if (isGetSalutationListFailed) {
			redirectToLoginBasedOnResponse(getSalutationListError, getSalutationList);
			setSalutationList([]);
		}
		// eslint-disable-next-line
	}, [
		getSalutationList,
		isGetSalutationListSuccess,
		isGetSalutationListFailed,
		t,
		getSalutationListError,
	]);

	useEffect(() => {
		refetchSalutationList();
	}, [refetchSalutationList]);

	const onChangeFirstName = (e: React.ChangeEvent<HTMLInputElement>) => {
		setBasicDetails({
			...basicDetailsRef?.current,
			firstName: convertToTitleCase(
				e.target.value.replace(VALIDATION_REGEXPS.matchesAnyCharacters, "")
			),
		});
	};

	const onChangeLastName = (e: React.ChangeEvent<HTMLInputElement>) => {
		setBasicDetails({
			...basicDetailsRef?.current,
			lastName: convertToTitleCase(e.target.value),
		});
	};

	const onBlurTrimEnd = (key: string) => (e: React.FocusEvent<HTMLInputElement | HTMLTextAreaElement>) => {
		if (key === "correspondenceAddress" && isAddressSame()) {
			setBasicDetails({
				...basicDetailsRef?.current,
				correspondenceAddress: e.target.value.trimEnd(),
				permanentAddress: e.target.value.trimEnd(),
			});
		} else {
			setBasicDetails({
				...basicDetailsRef?.current,
				[key]: e.target.value.trimEnd(),
			});
		}
	};

	const onChangeFathersName = (e: React.ChangeEvent<HTMLInputElement>) => {
		setBasicDetails({
			...basicDetailsRef?.current,
			fatherOrHusbandName: e.target.value.replace(
				VALIDATION_REGEXPS.matchesAnyCharacters,
				""
			),
		});
	};
	const handleDateChange = (e: React.ChangeEvent<HTMLInputElement>) => {
		setBasicDetails({
			...basicDetailsRef?.current,
			dob: e.target.value,
		});
	};

	const onChangeGender = (event: React.ChangeEvent<HTMLInputElement>) => {
		setBasicDetails({
			...basicDetailsRef?.current,
			gender: (event.target as HTMLInputElement).value,
		});
	};

	const onChangeMobile = (event: React.ChangeEvent<HTMLInputElement>) => {
		setBasicDetails({
			...basicDetailsRef?.current,
			mobile: event.target.value
				.replace(VALIDATION_REGEXPS.matchesAnyPhoneNumbers, "")
				.replace(/(?!^)\+/g, ""),
			mobileErrorMsg: "",
		});
	};

	const onChangeAlternativeMobile = (
		event: React.ChangeEvent<HTMLInputElement>
	) => {
		setBasicDetails({
			...basicDetailsRef?.current,
			alternativeMobile: event.target.value
				.replace(VALIDATION_REGEXPS.matchesAnyPhoneNumbers, "")
				.replace(/(?!^)\+/g, ""),
			mobileErrorMsg: "",
		});
	};

	const onChangeEmail = (event: React.ChangeEvent<HTMLInputElement>) => {
		setBasicDetails({
			...basicDetailsRef?.current,
			email: event.target.value
				.replace(VALIDATION_REGEXPS.whiteSpacesRegex, "")
				.toLowerCase(),
			emailErrorMsg: "",
		});
	};

	const onChangeAgreementAddressType = (type: "Correspondence" | "Permanent") => {
		setBasicDetails({
			...basicDetailsRef?.current,
			agreementAddressType: type,
		});
	};

	const onChangeCorrespondenceAddress = (
		event: React.ChangeEvent<HTMLInputElement>
	) => {
		const val = event.target.value;
		const current = basicDetailsRef?.current;
		const isSame = isAddressSame();
		setBasicDetails({
			...current,
			correspondenceAddress: val,
			...(isSame && { permanentAddress: val }),
		});
	};
	const onChangePermanentAddress = (event: { target: { value: string } }) => {
		const val = event.target.value;
		const current = basicDetailsRef?.current;
		setBasicDetails({
			...current,
			permanentAddress: val,
		});
	};

	const onChangePin = (event: React.ChangeEvent<HTMLInputElement>) => {
		if (isAddressSame()) {
			setBasicDetails({
				...basicDetailsRef?.current,
				pin: event.target.value.replace(
					VALIDATION_REGEXPS.matchesAnyNumbers,
					""
				),
				permanentPin: event.target.value.replace(
					VALIDATION_REGEXPS.matchesAnyNumbers,
					""
				),
			});
		} else {
			setBasicDetails({
				...basicDetailsRef?.current,
				pin: event.target.value.replace(
					VALIDATION_REGEXPS.matchesAnyNumbers,
					""
				),
			});
		}
	};

	const onChangeTelephone = (event: React.ChangeEvent<HTMLInputElement>) => {
		if (isAddressSame()) {
			setBasicDetails({
				...basicDetailsRef?.current,
				telephone: event.target.value.replace(
					VALIDATION_REGEXPS.matchesAnyNumbers,
					""
				),
				permanentTelephone: event.target.value.replace(
					VALIDATION_REGEXPS.matchesAnyNumbers,
					""
				),
			});
		} else {
			setBasicDetails({
				...basicDetailsRef?.current,
				telephone: event.target.value.replace(
					VALIDATION_REGEXPS.matchesAnyNumbers,
					""
				),
			});
		}
	};

	const onChangePermanentPin = (event: React.ChangeEvent<HTMLInputElement>) => {
		setBasicDetails({
			...basicDetailsRef?.current,
			permanentPin: event.target.value.replace(
				VALIDATION_REGEXPS.matchesAnyNumbers,
				""
			),
		});
	};

	const onChangePermanentTelephone = (
		event: React.ChangeEvent<HTMLInputElement>
	) => {
		setBasicDetails({
			...basicDetailsRef?.current,
			permanentTelephone: event.target.value.replace(
				VALIDATION_REGEXPS.matchesAnyNumbers,
				""
			),
		});
	};

	const onChangeYesOrNo = (event: React.ChangeEvent<HTMLInputElement>) => {
		const isSame = event.target.value === "YES";
		const current = basicDetailsRef?.current;
		const newType = current?.agreementAddressType || "Correspondence";

		if (isSame) {
			setBasicDetails({
				...current,
				isPermanentAddressSameAsCorrespondence: "YES",
				permanentAddress: current?.correspondenceAddress || "",
				permanentPin: current?.pin || "",
				permanentTelephone: current?.telephone || "",
				agreementAddressType: newType,
			});
		} else {
			setBasicDetails({
				...current,
				isPermanentAddressSameAsCorrespondence: "NO",
				permanentAddress: "",
				permanentPin: "",
				permanentTelephone: "",
				agreementAddressType: newType,
			});
		}
	};

	const uploadPhoto = (
		data: string,
		file: { uri: string; type: string; size: number; name: string }
	) => {
		savePhoto(
			data,
			t("create_booking.photo", {
				applicant: numberOfApplicant,
				type: Boolean(getFileType(file?.name))
					? getFileType(file?.name)
					: file?.type,
			})
		);
		setBasicDetails({
			...basicDetailsRef?.current,
			photo: {
				name: t("create_booking.photo", {
					applicant: numberOfApplicant,
					type: Boolean(getFileType(file?.name))
						? getFileType(file?.name)
						: file?.type,
				}),
				type: file?.type,
				data: data,
			},
		});
	};

	const removePhoto = () => {
		savePhoto();
		setBasicDetails({
			...basicDetailsRef?.current,
			photo: {
				name: "",
				type: "",
				data: "",
			},
		});
	};

	const onChangeAddressSameAsPrimary = (
		event: React.ChangeEvent<HTMLInputElement>
	) => {
		setBasicDetails({
			...basicDetailsRef?.current,
			isAddressSameAsPrimaryApplicant: event.target.value,
		});
	};

	const getSalutationAsLabelAndValue = useMemo(
		() =>
			salutationList?.map((item: { salutation: string }) => {
				return { value: item?.salutation, label: item?.salutation };
			}),
		[salutationList]
	);

	const onChangeSalutation = (
		newValue: SingleValue<{
			value: string;
			label: string;
		}>,
		actionMeta: ActionMeta<{
			value: string;
			label: string;
		}>
	) => {
		setBasicDetails({
			...basicDetailsRef?.current,
			salutation: newValue?.value,
		});
	};

	const onChangeIsFathersName = useCallback((e: object, checked: boolean) => {
		setBasicDetails({
			...basicDetailsRef?.current,
			isFathersName: checked,
		});
	}, []);

	const renderName = () => (
		<div className="input-container" style={styles.textInputRowContainer}>
			<TextInput
				id="first-name-input"
				value={basicDetails.firstName}
				label={
					!basicDetailsRef?.current.firstName
						? t("create_booking.basic_details.first_name_placeholder")
						: ""
				}
				onChange={onChangeFirstName}
				onBlur={onBlurTrimEnd("firstName")}
				outerLabel={t("create_booking.basic_details.first_name")}
				markRequiredField={true}
				disabled={!isEditable}
			/>
			<div style={{ width: "10%" }} />
			<TextInput
				id="last-name-input"
				value={basicDetails.lastName}
				label={
					!basicDetailsRef?.current.lastName
						? t("create_booking.basic_details.last_name_placeholder")
						: ""
				}
				size="small"
				onChange={onChangeLastName}
				onBlur={onBlurTrimEnd("lastName")}
				outerLabel={t("create_booking.basic_details.last_name")}
				markRequiredField={true}
				disabled={!isEditable}
			/>
		</div>
	);

	const renderFatherOrHusbandNameInput = () => (
		<div className="input-container">
			<TextInput
				id="husband-spouse-name-input"
				value={basicDetails?.fatherOrHusbandName}
				label={
					!basicDetailsRef?.current.fatherOrHusbandName
						? t(
							"create_booking.basic_details.father_or_husband_name_placeholder"
						)
						: ""
				}
				onChange={onChangeFathersName}
				onBlur={onBlurTrimEnd("fatherOrHusbandName")}
				outerLabel={t("create_booking.basic_details.father_or_husband_name")}
				markRequiredField={true}
				disabled={!isEditable}
			/>
			<div style={{ width: "10%" }} />
			{isDOBRequired && (
				<DatePickerComponent
					date={basicDetails?.dob}
					onChangeDate={handleDateChange}
					outerLabel={t("create_booking.basic_details.date_of_birth")}
					markRequiredField={true}
					maxDate={moment(new Date()).format(YYYY_MM_DD_WITH_HYPHEN)}
					minDate={MINIMUM_DOB}
					disabled={!isEditable}
				/>
			)}
		</div>
	);

	const renderGender = () => (
		<RowRadioButtonsGroup
			title={t("create_booking.basic_details.gender")}
			labels={genders}
			value={basicDetailsRef?.current.gender}
			handleChange={onChangeGender}
			markRequiredField={false}
			sx={{ formLabel: { mt: "2rem" } }}
			disabled={!isEditable}
		/>
	);

	const isOpportunity = location.pathname.includes("opportunity");
	const isBooking = location.pathname.includes("booking");
	const [showMobile, setShowMobile] = useState(false);
	const [showAltMobile, setShowAltMobile] = useState(false);
	const [showEmail, setShowEmail] = useState(false);


	const renderMobileNumberAndEmail = () => (
		<div>
			<div className="input-container" style={styles.textInputRowContainer}>
				<TextInput
					id="mobile-input"
					autoComplete="off" // Prevent browser autofill
					value={
						// Mask if it's an opportunity AND in edit/view mode (not creation)
						isOpportunity && canEdit
							? maskPhone(basicDetails?.mobile)
							: basicDetails?.mobile
					}
					onChange={onChangeMobile}
					onBlur={onBlurTrimEnd("mobile")}
					error={Boolean(basicDetailsRef?.current.mobileErrorMsg)}
					helperText={basicDetailsRef?.current.mobileErrorMsg}
					outerLabel={t("create_booking.basic_details.mobile_number")}
					markRequiredField
					styles={{
						outerLabel: { marginTop: "2rem" },
						textInputContainer: basicDetailsRef?.current.mobileErrorMsg
							? { marginTop: 23 }
							: {},
					}}
					isMobile
					maxLength={16}
					// Disabled if it's an opportunity AND in edit/view mode,
					// OR if the overall component is not editable
					disabled={(isOpportunity && canEdit) || !isEditable}
				/>

				<div style={{ width: "10%" }} />

				{/* Alternative mobile input: ONLY RENDERED IF IT'S AN OPPORTUNITY */}
				{isOpportunity && ( // This condition completely hides it for bookings
					<TextInput
						id="alt-mobile-input"
						// The value is ALWAYS the raw value for alternative mobile
						value={basicDetails?.alternativeMobile || ""}
						onChange={onChangeAlternativeMobile}
						onBlur={onBlurTrimEnd("alternativeMobile")}
						// onFocus and onBlur are no longer needed as there's no masking to toggle
						outerLabel={t("create_booking.basic_details.alternative_mobile_number")}
						markRequiredField={false} // Non-mandatory
						error={altRequiredError}
						helperText=""
						styles={{
							outerLabel: { marginTop: "2rem" },
							textInputContainer: basicDetailsRef?.current.mobileErrorMsg
								? { marginTop: 23 }
								: {},
						}}
						isMobile
						maxLength={16}
						// Alternative mobile is always editable as long as the parent component is editable
						disabled={!isEditable}
					/>
				)}
			</div>
			<TextInput
				id="email-input"
				autoComplete="off" // Prevent browser autofill
				value={
					// Mask if it's an opportunity AND in edit/view mode (not creation)
					isOpportunity && canEdit
						? maskEmail(basicDetails?.email)
						: basicDetails?.email
				}
				label={
					!basicDetailsRef?.current.email
						? t("create_booking.basic_details.email")
						: ""
				}
				onChange={onChangeEmail}
				onBlur={onBlurTrimEnd("email")}
				error={Boolean(basicDetailsRef?.current.emailErrorMsg)}
				helperText={basicDetailsRef?.current.emailErrorMsg}
				outerLabel={t("create_booking.basic_details.email")}
				markRequiredField={isEmailMandatory}
				type={"email"}
				styles={{
					outerLabel: {
						marginTop: "2rem",
					},
					textInputContainer: {
						...(basicDetailsRef?.current.emailErrorMsg && {
							marginTop: 23,
						}),
					},
				}}
				// Disabled if it's an opportunity AND in edit/view mode,
				// OR if the overall component is not editable
				disabled={(isOpportunity && canEdit) || !isEditable}
			/>
		</div>
	);
	const renderCorrespondenceAddress = () => (
		<div className="input-container">
			<TextInput
				id="correspondence-address"
				value={basicDetails?.correspondenceAddress}
				label={
					!basicDetailsRef?.current.correspondenceAddress
						? t(
							"create_booking.basic_details.correspondence_address_placeholder"
						)
						: ""
				}
				size="medium"
				onChange={onChangeCorrespondenceAddress}
				onBlur={onBlurTrimEnd("correspondenceAddress")}
				outerLabel={t("create_booking.basic_details.correspondence_address")}
				markRequiredField={true}
				multiline={true}
				rows={4}
				disabled={!isEditable}
				{...(numberOfApplicant > 1 && {
					styles: {
						outerLabel: { marginTop: 20 },
					},
				})}
			/>
		</div>
	);

	const renderPermanentAddress = () => (
		<div className="input-container">
			<TextInput
				id="permanent-address"
				value={basicDetails?.permanentAddress}
				label={
					!basicDetailsRef?.current.permanentAddress
						? t("create_booking.basic_details.permanent_address_placeholder")
						: ""
				}
				size="medium"
				onChange={onChangePermanentAddress}
				onBlur={onBlurTrimEnd("permanentAddress")}
				outerLabel={t("create_booking.basic_details.permanent_address")}
				markRequiredField={true}
				multiline={true}
				styles={{
					outerLabel: {
						marginTop: "1rem",
					},
				}}
				rows={4}
				disabled={!isEditable || isAddressSame()}
			/>
		</div>
	);

	const renderAgreementAddressSelectionGroup = () => (
		<RowRadioButtonsGroup
			title={t("create_booking.basic_details.address_for_agreement_process")}
			labels={[
				{ id: "Correspondence", label: "Correspondence Address" },
				{ id: "Permanent", label: "Permanent Address" },
			]}
			value={basicDetailsRef?.current?.agreementAddressType || basicDetails?.agreementAddressType}
			handleChange={(e) => onChangeAgreementAddressType(e.target.value as "Correspondence" | "Permanent")}
			markRequiredField
			sx={{ formLabel: { mt: "2rem" }, labelRequired: { color: "red" } }}
			disabled={!isEditable}
		/>
	);

	const renderPinAndTelPhone = () => (
		<div className="input-container">
			<TextInput
				id="correspondence-pin-input"
				value={basicDetails?.pin}
				label={
					!basicDetailsRef?.current.pin
						? t("create_booking.basic_details.pin_placeholder")
						: ""
				}
				onChange={onChangePin}
				outerLabel={t("create_booking.basic_details.pin")}
				markRequiredField={true}
				disabled={!isEditable}
				maxLength={MAXIMUM_PIN_LENGTH}
			/>
			<div style={{ width: "10%" }} />
			<TextInput
				id="correspondence-tel-input"
				value={basicDetails?.telephone}
				label={
					!basicDetailsRef?.current.telephone
						? t("create_booking.basic_details.tel_placeholder")
						: ""
				}
				onChange={onChangeTelephone}
				outerLabel={t("create_booking.basic_details.tel")}
				markRequiredField={false}
				disabled={!isEditable}
			/>
		</div>
	);

	const renderPermanentPin = () => (
		<div className="input-container">
			<TextInput
				id="permanent-pin-input"
				value={basicDetails?.permanentPin}
				label={
					!basicDetailsRef?.current.permanentPin
						? t("create_booking.basic_details.pin_placeholder")
						: ""
				}
				onChange={onChangePermanentPin}
				outerLabel={t("create_booking.basic_details.pin")}
				markRequiredField={true}
				disabled={!isEditable || isAddressSame()}
				maxLength={MAXIMUM_PIN_LENGTH}
			/>
		</div>
	);

	const renderYesOrNoRadioButton = () => (
		<RowRadioButtonsGroup
			title={t("create_booking.basic_details.permanent_address_same_query")}
			labels={[
				{ id: "YES", label: "Yes" },
				{ id: "NO", label: "No" },
			]}
			value={basicDetailsRef?.current.isPermanentAddressSameAsCorrespondence}
			handleChange={onChangeYesOrNo}
			markRequiredField
			sx={{ formLabel: { mt: "2rem" }, labelRequired: { color: "red" } }}
			disabled={!isEditable}
		/>
	);

	const renderCapturedOrUploadedPhoto = () => (
		<div className="single-detail-container">
			<ApplicantPhoto
				uploadPhoto={uploadPhoto}
				photo={basicDetailsRef?.current.photo}
				removePhoto={removePhoto}
				numberOfApplicant={numberOfApplicant.toString()}
				disabled={!isEditable}
				title={t("create_booking.basic_details.photo")}
				type={t("create_booking.basic_details.face")}
				placeholder={t("create_booking.basic_details.photo")}
			/>
		</div>
	);

	const renderIsAddressSameQuery = () => (
		<RowRadioButtonsGroup
			title={t(
				"create_booking.basic_details.is_address_same_as_primary_applicant"
			)}
			labels={[
				{ id: "YES", label: "Yes" },
				{ id: "NO", label: "No" },
			]}
			value={basicDetailsRef?.current.isAddressSameAsPrimaryApplicant}
			handleChange={onChangeAddressSameAsPrimary}
			markRequiredField={false}
			sx={{ formLabel: { mt: "2rem" } }}
			disabled={!isEditable}
		/>
	);

	const renderSalutationList = () => (
		<Dropdown
			outerLabel={t("create_booking.basic_details.salutation")}
			markRequiredField={false}
			dropdownItems={getSalutationAsLabelAndValue}
			inputLabel={t("create_booking.basic_details.salutation_placeholder")}
			handleChange={onChangeSalutation}
			value={basicDetails?.salutation}
			isDisabled={!isEditable}
			styles={{ textInputContainer: { width: "26%" } }}
		/>
	);

	const renderIsFathersName = () => (
		<FormGroup sx={{ alignSelf: "flex-start" }}>
			<Label
				disableTypography
				control={
					<StyledCheckBox
						icon={<CircleOutlined />}
						checkedIcon={<CheckCircleIcon />}
						value={basicDetailsRef.current?.isLoanSanctioned}
						sx={{ p: 0, pr: 1 }}
						size={"small"}
					/>
				}
				label={t("create_booking.basic_details.father_name")}
				labelPlacement="end"
				checked={basicDetailsRef?.current.isFathersName}
				onChange={onChangeIsFathersName}
				value={basicDetailsRef.current?.isFathersName}
				sx={{ color: Colors.greyColor }}
				disabled={!isEditable}
			/>
		</FormGroup>
	);

	return (
		<>
			{isSalutationRequired && renderSalutationList()}
			{renderName()}
			{isFatherNameRequired && renderFatherOrHusbandNameInput()}
			{isFatherNameRequired && renderIsFathersName()}
			{isGenderRequired && renderGender()}
			{renderMobileNumberAndEmail()}
			{numberOfApplicant > 1 && renderIsAddressSameQuery()}
			{isAddressRequired && !isAddressSameAsPrimaryApplicant() && (
				<>
					{renderCorrespondenceAddress()}
					{renderPinAndTelPhone()}
					{renderYesOrNoRadioButton()}
					{renderPermanentAddress()}
					{renderPermanentPin()}
					{renderAgreementAddressSelectionGroup()}
				</>
			)}
			{isPhotoRequired && renderCapturedOrUploadedPhoto()}
		</>
	);
};
export default ApplicantBasicDetailsForm;
